<template>
  <main class="main main--home main--full-height">
    <module-modal v-if="isIe" :modal="oldBrowser" />

    <transition-group name="confirm" mode="out-in">
      <div v-if="cancelling === false" key="main" class="group group--last">
        <module-pop-up v-if="userLoggedOut" v-bind="forcedLogout" />
        <div class="grid grid--home">
          <div class="grid__item grid__item--2 pr hide-below-medium">
            <div class="home__image">
              <img :src="image">
            </div>
          </div>

          <div class="grid__item grid__item--2">
            <transition name="intake" mode="out-in">
              <text-carousel
                v-if="org && org.username || isAuthenticated"
                :phrase="searchIsOpen ? {
                  id: 1,
                  headline: 'Find your company',
                  sub_headline: 'Visit your company’s Beacon portal to log in and access your Carelon Wellbeing services.',
                } : activeHomepagePhrase"
              />
            </transition>

            <div class="mt0 mb2">
              <blockquote class="mb" v-if="!org.username">
                <p class="p mt0 mb0">
                  Please contact your benefits administrator for assistance.
                </p>
              </blockquote>
              <module-button
                v-if="copy.buttonShow"
                @click.native="handleButtonAction(copy)"
                :button="copy.buttonType"
                size="s"
              >{{ copy.button }}
              </module-button>

              <module-button
                v-if="copy.button === `Continue`"
                @click.native="restartIntake(copy)"
                button="secondary"
              >Restart</module-button
              >

              <module-button
                :loading="cancelling"
                v-if="copy.showCancel && isAuthenticated"
                @click.native="rescheduleAppointment()"
                button="secondary"
                class="mr-tiny"
                size="s"
              >
                Reschedule
              </module-button>

              <module-button
                :loading="cancelling"
                v-if="copy.showCancel && isAuthenticated"
                @click.native="
                  () => {
                    cancelling = 'confirm';
                  }
                "
                button="secondary"
                class="mr-tiny"
                size="s"
              >
                Cancel
              </module-button>

              <module-button
                v-if="isAuthenticated && appointments.length > 0"
                :loading="cancelling"
                link="appointments"
                button="primary"
                size="s"
              >
                View All Appointments
              </module-button>
            </div>
            <blockquote class="mb" v-if="org.introduction">
              <p class="p mt0 mb0">{{ org.introduction }}</p>
            </blockquote>

            <module-nav-links
              :search="isRootOrg"
              :services="services"
              :copy="copy"
              :hr="false"
              @isSearching="isSearching"
            />
          </div>
          <div class="home__image hide-above-medium">
            <img :src="image" >
          </div>
        </div>
      </div>

      <template v-if="cancelling === 'confirm'">
        <div class="bg-primary ovh df aic jcc pd" key="confirm">
          <div>
            <h1 
              :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
              class="c-accent">Are you sure you want to cancel?</h1>
            <p class="p">
              You might not be able to get another appointment at the same time
              or with the same counselor.
            </p>
            <module-button
              @click.native="cancelAppointment(copy.id)"
              button="secondary"
            >
              Yes, Cancel
            </module-button>
            <module-button
              @click.native="
                () => {
                  cancelling = false;
                }
              "
              button="secondary"
            >
              No, never mind
            </module-button>
          </div>
        </div>
      </template>

      <ui-module-loader
        key="loader"
        @messages-complete="onMessagesComplete"
        v-if="cancelling === 'loading'"
        :messages="[
          'Please give us a moment to cancel',
          'All set, your appointment has been cancelled'
        ]"
      />
    </transition-group>
  </main>
</template>

<script>

// Vuex
import { mapGetters, mapActions } from 'vuex';

// Models
import meta from 'models/meta';

// Modules
import ModuleLogo from 'components/Logo';
import ModuleServiceItem from 'components/ServiceItem';
import ModuleNavLinks from 'components/NavLinks';
import UiModuleLoader from 'components/ModuleLoader';
import ModulePopUp from 'components/PopUp';
import ModuleModal from 'components/Modal';
import TextCarousel from 'components/TextCarousel';

// Packages
import moment from 'moment';

// Utils
import { createMeta } from 'utils/meta';
import { parsePersonalPhrase } from 'utils/personalPhrases';

import phone from 'utils/phone';

// Transitions
import TransitionExpandHeight from 'components/ExpandHeight';

import illustration from 'images/home/home-illustration.png';

// Images
const images = {
  illustration,
};

// Export
export default {
  name: 'view-home',

  // Meta data
  metaInfo: {
    title: meta.home.title,
    meta: createMeta(meta.home.meta),
  },

  // Components
  components: {
    TransitionExpandHeight,
    ModuleLogo,
    ModuleNavLinks,
    ModuleServiceItem,
    ModulePopUp,
    ModuleModal,
    UiModuleLoader,
    TextCarousel,
  },

  // Data
  data() {
    return {
      currentPhraseIndex: 0,
      cancelling: false,
      image: images.illustration,
      isVideo: false,

      searchIsOpen: false,

      oldBrowser: {
        title: 'Unsupported Browser Detected',
        copy:
          'In order to continue, please use <a class="link fw-bold c-accent tdn" target="_blank" href="https://www.apple.com/safari/">Safari</a>, <a class="link fw-bold c-accent tdn" target="_blank" href="https://www.mozilla.org/firefox">Firefox</a>, <a class="link fw-bold c-accent tdn" target="_blank" href="//www.google.com/chrome/">Chrome</a> or <a class="link fw-bold c-accent tdn" target="_blank" href="//www.microsoft.com/edge">Edge</a>.',
      },

      homePhrases: parsePersonalPhrase,

      home: {
        title: 'We\'re here to help you',
        copy:
          'With a private conversation with the right person. It only takes a few minutes to find an expert and schedule a time to talk.',
        show: false,
      },

      appointmentPolling: null,
      appointmentPollingInterval: 60000,

      forcedLogout: {
        header: 'You have been logged out',
        copy:
          'For privacy and security you have been logged out due to inactivity',
        button: {
          link: 'login',
          label: 'Log In',
        },
      },
    };
  },
  computed: {
    ...mapGetters('auth', ['isAuthenticated', 'user']),
    ...mapGetters('forms', ['getFieldModel']),
    ...mapGetters('appointment', ['appointments', 'f2fAppointment']),
    ...mapGetters('app', ['org']),
    ...mapGetters('answers', ['startIntakeTimeStamp']),
    ...mapGetters('home', ['homepagePhrases']),
    ...mapGetters('provider', ['provider']),
    ...mapGetters('messaging', ['providerList']),

    isRootOrg() {
      return this.org.username === 'is_root_org';
    },

    isIe() {
      return (
        this.$browserDetect.meta.name === 'IE' &&
        this.$browserDetect.meta.version < '12'
      );
    },

    userLoggedOut() {
      return JSON.parse(localStorage.getItem('forcedLogout')) === true;
    },

    currentAppointment() {
      return this.appointments[0];
    },

    activeHomepagePhrase() {
      if (
        !this.isAuthenticated &&
        this.homepagePhrases &&
        this.homepagePhrases.length &&
        !localStorage.getItem('startIntakeTimeStamp')
      ) {
        return this.homepagePhrases[this.currentPhraseIndex];
      }

      return {
        id: 1,
        headline: this.copy.title,
        sub_headline: this.copy.copy,
      };
    },

    copy() {
      let home = {};
      if (
        this.appointments.length > 0 && this.isAuthenticated &&
        moment().diff(
          moment(this.appointments[this.appointments.length - 1].start_date),
          'hour'
        ) < 1
      ) {
        const appointment = this.appointments[0];
        const time = {
          currentTime: moment(new Date().toISOString()).format(),
          appointmentTime: appointment.start_date,
        };

        let date = '';

        if (
          moment(time.currentTime).isSame(moment(time.appointmentTime), 'day')
        ) {
          date = 'today';
        } else if (
          moment(time.appointmentTime) <
          moment()
            .add(1, 'day')
            .endOf('day')
        ) {
          date = 'tomorrow';
        } else {
          date = `on ${moment(appointment.start_date).format('dddd, MMMM Do')}`;
        }

        const soon =
          moment(time.appointmentTime).diff(
            moment(time.currentTime),
            'minutes'
          ) <= 5 &&
          moment(time.appointmentTime).diff(
            moment(time.currentTime),
            'minutes'
          ) >= 0;
        const isJoinActive =
          moment(time.appointmentTime).diff(
            moment(time.currentTime),
            'minutes'
          ) <= 5;
        const current =
          moment(time.appointmentTime).diff(
            moment(time.currentTime),
            'minutes'
          ) <= -1;
        const appointmentType = appointment.appointment_by;
        const cta =
          appointmentType === 'video'
            ? `will join at ${moment(appointment.start_date).format('h:mma')}`
            : `will call you directly at ${phone(
              appointment.customer_call_in_number
            )}`;
        const chat = appointmentType === 'video' ? 'video chat' : 'phone call';
        const buildTitle = soon
          ? `Your ${chat} is about to begin`
          : `You have a ${chat} ${date} at ${moment(
            appointment.start_date
          ).format('h:mma')}`;
        const buildSubTitle =
          appointmentType === 'video' && !soon
            ? `At your scheduled time with ${appointment.provider.full_name}, your "Join Video Chat" button will appear below.`
            : `Your counselor, ${appointment.provider.full_name}, ${cta}`;

        home = {
          title: !current
            ? buildTitle
            : appointmentType === 'video'
              ? 'Your counselor is still available for your video chat'
              : `Your counselor called you at ${moment(
                time.appointmentTime
              ).format('h:mma')}`,
          copy:
            current && appointmentType === 'phone'
              ? `If you missed your phone call or got disconnected, please call us at ${phone(
                this.org && this.org.phone
              )} and we'll find another time for you to talk.`
              : `${
                appointment.provider_ready ? 'Please join now. ' : ''
              } ${buildSubTitle}`,
          button: 'Join Video Chat',
          buttonType: 'primary',
          buttonShow: isJoinActive && appointmentType === 'video',
          action: 'openAppointment',
          show: true,
          cancel: !soon,
          showCancel: !isJoinActive,
          id: appointment.id,
          appointmentType,
        };
      } else if (
        this.f2fAppointment &&
        moment().diff(moment(this.f2fAppointment.modified_at), 'hour') < 48 &&
        this.f2fAppointment.show_homepage_message
      ) {
        home = this.home;
        home.title = `${this.homePhrases()}.`;
        home.copy = `You requested an in-person session. If you haven’t heard from us already, our team should be in touch soon. If you don’t hear anything within 48 hours, please call ${phone(
          this.org.phone
        )}.`;
      } else {
        home = this.home;
        if (this.isAuthenticated && localStorage.getItem('updateUser')) {
          home.title = this.homePhrases();
          home.copy = '';
        }
      }

      if (localStorage.getItem('startIntakeTimeStamp')) {
        home = {
          title: 'Looks like you didn’t finish your questionnaire last time',
          button: 'Continue',
          buttonType: 'primary',
          buttonShow: true,
          show: true,
          action: 'continueIntake',
        };
      }

      return home;
    },

    services() {
      if (this.org && this.org.homepage_navs && !this.org.dfd_client) {
        return _.forEach(this.org.homepage_navs, nav => {
          // eslint-disable-line no-undef
          if (nav.url === '/support' || nav.url === '/appointment-type') {
            nav.url = localStorage.getItem('updateUser')
              ? '/appointment-type'
              : '/support';
            nav.hide = this.copy.show;
          }
        });
      }
      if (this.org && this.org.homepage_navs && this.org.dfd_client && !this.isAuthenticated) {
        return _.forEach(this.org.homepage_navs, nav => {
          // eslint-disable-line no-undef
          if (nav.url === '/log-in' || nav.url === '/support') {
            nav.url = localStorage.getItem('updateUser')
              ? '/support'
              : '/log-in';
            nav.hide = this.copy.show;
          }
        });
      }
      if (this.org && this.org.homepage_navs && this.org.dfd_client) {
        return _.forEach(this.org.homepage_navs, nav => {
          // eslint-disable-line no-undef
          if (nav.url === '/support' || nav.url === '/results') {
            nav.url = localStorage.getItem('updateUser')
              ? '/results'
              : '/support';
            nav.hide = this.copy.show;
          }
        });
      }
    },
  },

  // Watchers
  watch: {
    homepagePhrases(phrases) {
      if (phrases) {
        this.loopThroughHomepagePhrases();
      }
    },
  },

  // Methods
  methods: {
    ...mapActions('appointment', [
      'requestAppointmentUpcoming',
      'requestAppointmentF2fUpcoming',
      'requestVideoUrl',
      'requestCancelAppointment',
    ]),
    ...mapActions('answers', ['checkIntakeStatus']),
    ...mapActions('home', ['getHomepagePhrases']),
    ...mapActions('provider', ['saveSelectedProvider']),
    ...mapActions('app', ['requestOrg']),
    ...mapActions('messaging', ['fetchProviderList']),

    isSearching(v) {
      this.searchIsOpen = v;
    },

    handleButtonAction(content) {
      if (content.action) {
        if (content.id) {
          this[content.action](content.id);
        } else {
          this[content.action]();
        }
      }
    },

    openAppointment(id) {
      const win = window.open();
      this.requestVideoUrl(id).then(res => {
        win.location = res.url;
        win.focus();
      });
    },

    continueIntake() {
      localStorage.setItem('continuedIntake', true);

      localStorage.getItem('q_intakeDetailLastQuestion')
        ? this.$router.push({
          name: 'support-detail',
          params: { id: localStorage.getItem('q_intakeDetailLastQuestion') },
        })
        : this.$router.push({
          name: 'support',
        });
    },

    restartIntake() {
      const stored = ['Who', 'Location', 'Complaint', 'SecondaryComplaint'];

      stored.forEach((item, index) => {
        localStorage.removeItem(`object${stored[index]}`);
        localStorage.removeItem(`answer${stored[index]}`);
      });
      localStorage.removeItem('continuedIntake');
      localStorage.removeItem('startIntakeTimeStamp');
      localStorage.removeItem('isSafe');

      _.forEach(
        _.filter(
          Object.keys(localStorage),
          key => key.split('_').shift() === 'q'
        ),
        questionnaire => {
          // eslint-disable-line no-undef
          localStorage.removeItem(questionnaire);
        }
      );
      this.$router.push({ name: 'support' });
    },

    onMessagesComplete() {
      this.cancelling = false;
      location.reload();
    },

    cancelAppointment(id) {
      this.cancelling = 'loading';
      const patient = JSON.parse(localStorage.getItem('updateUser'));
      const model = {
        id,
        patient: patient.mdlive_id,
      };

      localStorage.removeItem('rescheduling');
      localStorage.removeItem('rescheduleId');
      this.requestCancelAppointment(model);
    },

    rescheduleAppointment() {
      localStorage.setItem('rescheduleId', this.appointments[0].id);
      localStorage.setItem('rescheduling', true);
      this.saveSelectedProvider(this.currentAppointment.provider);
      this.$router.push({
        name: 'provider-schedule',
      });
    },

    loopThroughHomepagePhrases() {
      if (
        this.homepagePhrases &&
        this.homepagePhrases.length &&
        !localStorage.getItem('startIntakeTimeStamp') &&
        this.$route.name === 'home'
      ) {
        setTimeout(() => {
          if (this.currentPhraseIndex < this.homepagePhrases.length - 1) {
            this.currentPhraseIndex++;
          } else {
            this.currentPhraseIndex = 0;
          }
          this.loopThroughHomepagePhrases();
        }, 5000);
      }
    },

    pollAppointmentData() {
      if (!this.appointmentPolling) {
        this.requestAppointmentUpcoming();
        this.requestAppointmentF2fUpcoming();
      }

      this.appointmentPolling = setInterval(() => {
        this.requestAppointmentUpcoming();
        this.requestAppointmentF2fUpcoming();
      }, this.appointmentPollingInterval);
    },
  },

  created() {
    if (
      !this.isAuthenticated &&
      !localStorage.getItem('startIntakeTimeStamp')
    ) {
      this.getHomepagePhrases();
    }

    if (this.appointments.length < 0) {
      localStorage.removeItem('rescheduling');
    }

    if (this.isAuthenticated) {
      this.pollAppointmentData();
    }

    this.checkIntakeStatus();
    this.requestOrg();

    localStorage.removeItem('rescheduling');
  },

  beforeDestroy() {
    clearInterval(this.appointmentPolling);
  },

  mounted() {
    if(this.isAuthenticated) {
      this.fetchProviderList();
    }
    
  },

};
</script>



// WEBPACK FOOTER //
// Home.vue?18f49af3